import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Button from '../../components/ui/Button';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../../components/ui/Modal';
import SimpleLoader from '../../components/ui/SimpleLoader';
import { CaseInfo, useApiReload } from '../../context/apiRelaod';
import { useAuth } from '../../context/authContext';
import {
	CaseList,
	CaseUpdate,
	DeleteSoap,
	PatientFetch,
	SoapList,
} from '../../services/apiService';
import TableTemplate from '../../templates/common/TableParts.template';
import { AudioFileDataWithoutBlob, getAllData } from '../../utils/indexedDB';
import { dateFormat } from '../../utils/priceFormat.util';
import { IcaseProps } from './NewNoteCase';

interface Patient {
	id: string;
	name: string;
	emailId: string;
	MobileNo: string;
}

interface Case {
	id: string;
	timestamp: string;
	soapNotes: { id: string; note: string; timestamp: string }[];
}

const PatientPage = () => {
	const { planData } = useAuth();
	const { patientID, setPatientID, caseInfo, setcaseInfo, reloadNotes, setReloadNotes } = useApiReload();
	const { onLogout } = useAuth();
	const [caseData, setCaseData] = useState<any>();
	const [SoapResponseData, setSoapResponseData] = useState<any>();
	const [audios, setAudios] = useState<AudioFileDataWithoutBlob[]>([]);

	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const { response, call, loading } = CaseList();
	const { Soapcall, SoapResponse } = SoapList();
	const { SoapDel, soap_del } = DeleteSoap();
	const { Patient_call, Patient_loading, Patient_response, error } = PatientFetch();
	const columnHelper = createColumnHelper<any>();

	const columns = [
		columnHelper.accessor('noteType', {
			header: 'Note Type',

			cell: (info) => (
				<span
					className='primary-text cursor-pointer font-medium hover:text-blue-800 hover:underline'
					onClick={() => {
						const note = info.row.original;
						if (patientID && selectedCase) {
							navigate(
								`/patients/${patientID.id}/cases/${selectedCase.id}/soap/${note.id}`,
								{
									state: {
										patient: patientID,
										caseData: selectedCase,
										soapData: note,
										selectedSoapNoteId: note.id,
									},
								},
							);
							useReactTable;
						} else {
							toast.error('No patient or case selected.');
						}
					}}>
					{info.getValue()}
				</span>
			),
		}),
		columnHelper.accessor('createdAt', {
			header: 'Created At',
			cell: (info) => dateFormat(info.getValue()),
		}),
		columnHelper.display({
			id: 'actions',
			header: 'Actions',
			cell: (info) => {
				const note = info.row.original;
				const rowIndex = info.row.index; // Get the row index

				return rowIndex !== firstInitialNoteIndex ? (
					<div className='flex items-center gap-5'>
						<button
							className='text-red-500 hover:text-red-700'
							onClick={() => openDeleteModal(selectedCase?.id!, note.id)}>
							<svg
								xmlns='http://www.w3.org/2000/svg'
								width='24px'
								height='24px'
								viewBox='0 0 24 24'
								fill='none'>
								<path
									d='M10 12V17'
									stroke='#ff0000'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M14 12V17'
									stroke='#ff0000'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M4 7H20'
									stroke='#ff0000'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M6 10V18C6 19.6569 7.34315 21 9 21H15C16.6569 21 18 19.6569 18 18V10'
									stroke='#ff0000'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z'
									stroke='#ff0000'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								removeDataByPatientId
							</svg>
						</button>
					</div>
				) : null;
			},
		}),
	];
	const table = useReactTable({
		columns,
		data: SoapResponseData?.data || [],
		getCoreRowModel: getCoreRowModel(),
	});

	useEffect(() => {
		if (id) {
			Patient_call(id).catch((e) => {
				if (e?.status === 409) {
					onLogout(e?.status);
				}
			});
			call(id).subscribe({
				next: (res: any) => {
					setCaseData(res?.data);
				},
				error: (e) => {
					if (e?.status === 409) {
						onLogout(e?.status);
					}

					console.error('An error occurred:', e);
				},
			});
		}
	}, [id]);

	useEffect(() => {
		(async () => {
			const data = await getAllData();
			const filterData = data.filter((d: any) => d.patientId === patientID?.id);
			setAudios(filterData);
			// console.log(filterData);
		})();
	}, [patientID]);
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [patientID]);

	useEffect(() => {
		if (Patient_response) {
			setPatientID({
				firstName: Patient_response.data.firstName,
				lastName: Patient_response.data.lastName,
				email: Patient_response.data.email,
				id: Patient_response.data.id,
				moblieNo: Patient_response.data.mobile,
			});

			setupdatevalue(null);
		}
	}, [Patient_response, setPatientID]);

	const [patient, setPatient] = useState<Patient | null>(null);
	const [selectedCase, setSelectedCase] = useState<CaseInfo | null>(null);
	const [caseIdInput, setCaseIdInput] = useState<string>(''); // Always use string
	const selectedPatientId: number | null = 1;
	const [showModal, setShowModal] = useState(false);
	const [noteToDelete, setNoteToDelete] = useState<{ caseId: string; noteId: string } | null>(
		null,
	); // Changed to string for caseId
	const { Case_Update } = CaseUpdate();
	const [updatevalue, setupdatevalue] = useState<string | null>();

	// Effect to reload SoapList when a notification is completed
	useEffect(() => {
		if (reloadNotes && selectedCase && selectedCase.id) {
			// Reset the reload flag
			setReloadNotes(false);
			
			// Reload the SOAP notes for the current case without navigation
			Soapcall(selectedCase.id).subscribe({
				next: (value: any) => {
					setSoapResponseData(value);
				},
				error: (error) => {
					if (error?.status === 409) {
						onLogout(error?.status);
					}
					console.error('An error occurred:', error);
				},
			});
		}
	}, [reloadNotes, selectedCase, Soapcall, onLogout, setReloadNotes]);

	const handleCaseClick = (caseId: string) => {
		const foundCase = caseData.find((c: any) => c.id === caseId); // Compare using string IDs
		if (foundCase) {
			setcaseInfo(foundCase);
			setCaseIdInput(foundCase.id);
			setupdatevalue(foundCase.caseName);
			setSelectedCase(foundCase);

			Soapcall(foundCase.id).subscribe({
				next: (value: any) => {
					setSoapResponseData(value);
				},
				error: (error) => {
					if (error?.status === 409) {
						onLogout(error?.status);
					}
					console.error('An error occurred:', error);
				},
			});
		}
	};

	const openDeleteModal = (caseId: string, noteId: string) => {
		// Use string for caseId
		setNoteToDelete({ caseId, noteId });
		setShowModal(true);
	};

	const handlecaseupdate = () => {
		const data: IcaseProps = {
			caseId: caseInfo?.id!,
			caseName: updatevalue!,
		};
		const newId = updatevalue;

		if (!newId) {
			toast.error('Please enter a valid ID.');
			return;
		}

		if (caseInfo?.caseName === newId) {
			toast.error('Case ID already exists. Please enter a new ID.');
		} else if (selectedCase) {
			Case_Update(data).subscribe({
				error: (e) => {
					if (e?.status === 409) {
						onLogout(e?.status);
					}
					toast.error(e?.msg);
				},
			});
			setCaseData((prevCases: any) =>
				prevCases.map((c: any) =>
					c.caseName === selectedCase.caseName ? { ...c, caseName: newId } : c,
				),
			);
			setSelectedCase({ ...selectedCase, caseName: newId });
			toast.success('Case ID updated successfully!');
		}
	};

	const closeModal = () => {
		setShowModal(false);
		setNoteToDelete(null);
	};

	const handleDeleteNote = () => {
		if (noteToDelete) {
			soap_del(noteToDelete.noteId).subscribe({
				next: (value) => {
					let soapDelete = SoapResponseData?.data?.findIndex(
						(patient: any) => patient.id === noteToDelete.noteId,
					);
					if (soapDelete !== -1) {
						// Remove the element at the index
						SoapResponseData?.data?.splice(soapDelete, 1);
					}
					// Show success toast
					toast.success('SOAP note deleted successfully!');
					// Reset modal and selected note
					setShowModal(false);
					setNoteToDelete(null);
				},
				error: (error) => {
					if (error?.status === 409) {
						onLogout(error?.status);
					}
					// Handle error case
					console.error('Error deleting SOAP note:', error);
					toast.error('Failed to delete SOAP note. Please try again.');
				},
			});
		}
	};

	const initialNote = SoapResponseData?.data.filter((note: any) =>
		note.noteType?.startsWith('INITIAL_NOTE'),
	).length;
	let firstInitialNoteIndex: any;
	if (initialNote === 1) {
		firstInitialNoteIndex = SoapResponseData?.data.findIndex((note: any) =>
			note.noteType?.startsWith('INITIAL_NOTE'),
		);
	}

	// let firstInitialNoteIndex :any ;
	// const initialNote  = SoapResponseData?.data.filter((note: any) => note.noteType?.startsWith('INITIAL_NOTE'));
	// const FollowNote  = SoapResponseData?.data.filter((note: any) => note.noteType?.startsWith('FOLLOWUP_NOTE'));
	// const OrderSoapData = [...initialNote, ...FollowNote];
	// if(OrderSoapData.length === 1){firstInitialNoteIndex=SoapResponseData?.data.findIndex((note: any) => note.noteType?.startsWith('INITIAL_NOTE'));}

	if (Patient_loading) {
		return (
			<div className='flex h-screen items-center justify-center'>
				<SimpleLoader />
			</div>
		);
	}

	const hasRecordings = (caseId: string | undefined) => {
		if (!caseId) return false;
		return audios?.filter((d: any) => d.Soapid === caseId)?.length > 0;
	};

	return (
		<div className='min-h-screen bg-gray-50 dark:bg-slate-900'>
			<div className='container mx-auto px-4 pb-16 pt-6'>
				{/* Patient Card */}
				{patientID && (
					<div className='mb-6 rounded-lg bg-white p-6 shadow-md dark:bg-slate-800'>
						<div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
							<div className='flex flex-col'>
								<span className='text-sm text-gray-500 dark:text-gray-400'>
									Patient Name
								</span>
								<span className='text-lg font-medium text-gray-900 dark:text-white'>
									{patientID.firstName} {patientID.lastName}
								</span>
							</div>
							<div className='flex flex-col'>
								<span className='text-sm text-gray-500 dark:text-gray-400'>
									Email
								</span>
								<span className='text-lg font-medium text-gray-900 dark:text-white'>
									{patientID.email}
								</span>
							</div>
							<div className='flex flex-col'>
								<span className='text-sm text-gray-500 dark:text-gray-400'>
									Mobile Number
								</span>
								<span className='text-lg font-medium text-gray-900 dark:text-white'>
									{patientID.moblieNo}
								</span>
							</div>
						</div>
					</div>
				)}

				{/* Cases Section */}
				<div className='mb-6 rounded-lg bg-white p-6 shadow-md dark:bg-slate-800'>
					<div className='mb-6 flex items-center justify-between'>
						<h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
							Patient Cases
						</h2>
						<Button
							variant='solid'
							colorIntensity='600'
							className='primary-bg relative'
							onClick={() => {
								setcaseInfo(null);
								navigate(
									`/patients/${patientID?.id}/cases/${patientID?.id}-1/new-note-case`,
								);
							}}>
							<span className='flex items-center gap-2'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									width='16'
									height='16'
									viewBox='0 0 24 24'
									fill='none'
									stroke='currentColor'
									strokeWidth='2'
									strokeLinecap='round'
									strokeLinejoin='round'>
									<path d='M12 5v14M5 12h14' />
								</svg>
								New Case
							</span>
							{audios?.filter((d: any) => d.Soapid === '-1')?.length > 0 && (
								<span className='absolute -right-1 -top-1 flex h-3 w-3'>
									<span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75'></span>
									<span className='relative inline-flex h-3 w-3 rounded-full bg-red-500'></span>
								</span>
							)}
						</Button>
					</div>

					{loading ? (
						<div className='flex justify-center py-8'>
							<SimpleLoader />
						</div>
					) : (
						<div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
							{caseData?.length > 0 ? (
								caseData?.map((c: any, index: any) => (
									<div
										key={c.id}
										onClick={() => handleCaseClick(c.id)}
										className={`relative cursor-pointer rounded-lg border ${
											String(selectedCase?.id) === String(c.id)
												? 'primary-border secondary-bgcard-opacity dark:border-blue-600 dark:bg-blue-900/20'
												: 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-slate-800 dark:hover:bg-slate-700'
										} p-4 shadow-sm transition-all duration-200`}>
										<div className='mb-2 flex items-start justify-between'>
											<span className='font-medium text-gray-700 dark:text-gray-200'>
												Case ID: {c.caseName}
											</span>
											{hasRecordings(c.id) && (
												<span className='flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-400'>
													<span className='block h-2 w-2 rounded-full bg-red-500'></span>{' '}
													REC
												</span>
											)}
										</div>
										<div className='text-sm text-gray-500 dark:text-gray-400'>
											Created: {dateFormat(c?.createdAt)}
										</div>
									</div>
								))
							) : (
								<div className='col-span-full rounded-lg border border-dashed border-gray-300 py-8 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400'>
									<p className='font-medium'>No cases found for this patient</p>
									<p className='mt-1 text-sm'>Create a new case to get started</p>
								</div>
							)}
						</div>
					)}
				</div>

				{/* Case Information Section */}
				{caseData && selectedCase && updatevalue !== null && (
					<div className='mb-6 rounded-lg bg-white p-6 shadow-md dark:bg-slate-800'>
						<h2 className='mb-6 text-xl font-semibold text-gray-900 dark:text-white'>
							Case Information
						</h2>

						<div className='mb-6 rounded-lg bg-gray-50 p-4 dark:bg-slate-900/50'>
							<div className='flex flex-wrap items-center gap-3'>
								<label
									htmlFor='case-id'
									className='font-medium text-gray-700 dark:text-gray-300'>
									Case ID:
								</label>
								<input
									id='case-id'
									type='text'
									value={updatevalue!}
									onChange={(e) => setupdatevalue(e.target.value)}
									className='rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-slate-800 dark:text-white'
								/>
								<Button
									variant='solid'
									colorIntensity='600'
									className='primary-bg text-white'
									onClick={() => handlecaseupdate()}>
									Update
								</Button>
							</div>
						</div>

						{/* SOAP Notes Section */}
						<div className='mb-6'>
							<div className='mb-4 flex items-center justify-between'>
								<h3 className='text-lg font-medium text-gray-900 dark:text-white'>
									SOAP Notes for Case {selectedCase?.caseName}
								</h3>
								<div className='flex gap-3'>
									<Button
										variant='solid'
										color='green'
										colorIntensity='600'
										className='relative'
										onClick={() => {
											if (patientID && selectedCase) {
												navigate(
													`/patients/${patientID.id}/cases/${caseInfo?.id}/new-note-case/`,
													{
														state: {
															NOTE: 'Followup',
														},
													},
												);
											} else {
												toast.error('No patient or case selected.');
											}
										}}>
										<span className='flex items-center gap-2'>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												width='16'
												height='16'
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'>
												<path d='M12 5v14M5 12h14' />
											</svg>
											Follow Up
										</span>
										{hasRecordings(selectedCase.id) && (
											<span className='absolute -right-1 -top-1 flex h-3 w-3'>
												<span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75'></span>
												<span className='relative inline-flex h-3 w-3 rounded-full bg-red-500'></span>
											</span>
										)}
									</Button>
								</div>
							</div>

							{SoapResponseData?.data?.length > 0 ? (
								<div className='overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700'>
									<div className='max-h-96 overflow-y-auto'>
										<TableTemplate
											table={table}
											hasFooter={false}
											isSticky={true}
											className='w-full'
										/>
									</div>
								</div>
							) : (
								<div className='rounded-lg border border-dashed border-gray-300 py-8 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400'>
									<p className='font-medium'>No SOAP notes found for this case</p>
									<p className='mt-1 text-sm'>
										Create a follow-up note to get started
									</p>
								</div>
							)}
						</div>
					</div>
				)}
			</div>

			{/* Delete Modal */}
			<Modal isOpen={showModal} setIsOpen={setShowModal} isCentered size='sm'>
				<ModalHeader setIsOpen={setShowModal}>Confirm Delete</ModalHeader>
				<ModalBody>
					<p className='text-base text-gray-700 dark:text-gray-300'>
						Are you sure you want to delete this Note? This action cannot be undone.
					</p>
				</ModalBody>
				<ModalFooter className='justify-end gap-3 pt-3'>
					<Button
						variant='outline'
						color='gray'
						colorIntensity='500'
						className='cursor-pointer'
						onClick={() => setShowModal(false)}>
						Cancel
					</Button>
					<Button
						variant='solid'
						color='red'
						colorIntensity='600'
						className='cursor-pointer text-white'
						onClick={() => {
							handleDeleteNote();
						}}>
						Delete
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
};

export default PatientPage;
